// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Button, TextField, Box, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, IconButton, CircularProgress } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabase';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import loginVideo from '../assets/LoginVideo.mp4';

const ResetPasswordPage: React.FC = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorDialog, setErrorDialog] = useState({ open: false, message: '' });
    const [successDialog, setSuccessDialog] = useState({ open: false, message: '' });
    const [tokenError, setTokenError] = useState(false);
    const [initializing, setInitializing] = useState(true);
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();



    // Extract token from URL if present
    useEffect(() => {
        const checkSession = async () => {
            try {
                setInitializing(true);


                // Check if we're in a recovery flow by checking the session
                const { data, error } = await supabase.auth.getSession();

                if (error) {
                    console.error('Session error:', error);
                    setTokenError(true);
                    setErrorDialog({
                        open: true,
                        message: 'Authentication session error. Please request a new password reset.'
                    });
                    return;
                }



                // If we have a valid session with access token but no user, we're likely in a recovery flow
                if (data?.session?.access_token) {

                } else {
                    // Check URL hash for recovery token
                    const hash = window.location.hash;
                    const query = new URLSearchParams(window.location.search);

                    // Special case for empty hash fragment which might be from Supabase redirect
                    if (hash === '#' && window.location.pathname.includes('/reset-password')) {

                        // This is probably fine, Supabase might have already processed the token
                        // We'll continue and let the updateUser call determine if the session is valid

                        // Try to get the access token from the URL
                        const accessToken = new URLSearchParams(hash.substring(1)).get('access_token');
                        if (accessToken) {

                            // You could set the session here if needed
                        }
                    } else if (!(hash && hash.includes('type=recovery')) &&
                        !(query.get('type') === 'recovery')) {
                        console.error('No recovery session or token found');
                        setTokenError(true);
                        setErrorDialog({
                            open: true,
                            message: 'Invalid or expired password reset link. Please request a new password reset.'
                        });
                    }
                }
            } catch (err) {
                console.error('Error checking session:', err);
                setTokenError(true);
                setErrorDialog({
                    open: true,
                    message: 'Error verifying your session. Please request a new password reset.'
                });
            } finally {
                setInitializing(false);
            }
        };

        checkSession();
    }, []);

    // Password validation
    const [passwordError, setPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');

    const validatePassword = (password: string) => {
        if (password.length < 8) {
            return 'Password must be at least 8 characters long';
        }
        if (!/[A-Z]/.test(password)) {
            return 'Password must contain at least one uppercase letter';
        }
        if (!/[a-z]/.test(password)) {
            return 'Password must contain at least one lowercase letter';
        }
        if (!/[0-9]/.test(password)) {
            return 'Password must contain at least one number';
        }
        if (!/[^A-Za-z0-9]/.test(password)) {
            return 'Password must contain at least one special character';
        }
        return '';
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        setPasswordError(validatePassword(newPassword));

        // Update confirm password error if confirm password is not empty
        if (confirmPassword) {
            setConfirmPasswordError(
                newPassword !== confirmPassword ? 'Passwords do not match' : ''
            );
        }
    };

    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newConfirmPassword = e.target.value;
        setConfirmPassword(newConfirmPassword);
        setConfirmPasswordError(
            newConfirmPassword !== password ? 'Passwords do not match' : ''
        );
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleClickShowConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate passwords
        const passwordValidationError = validatePassword(password);
        if (passwordValidationError) {
            setPasswordError(passwordValidationError);
            return;
        }

        if (password !== confirmPassword) {
            setConfirmPasswordError('Passwords do not match');
            return;
        }

        setLoading(true);

        try {


            // The updateUser method will use the token from the URL automatically
            // when called in the context of a recovery flow
            const { error } = await supabase.auth.updateUser({
                password: password
            });

            if (error) {
                console.error('Error updating password:', error);
                throw error;
            }


            setSuccessDialog({
                open: true,
                message: 'Password has been reset successfully!'
            });
        } catch (error) {
            console.error('Error resetting password:', error);
            setErrorDialog({
                open: true,
                message: error.message || 'Failed to reset password. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseErrorDialog = () => {
        setErrorDialog({ ...errorDialog, open: false });
        if (tokenError) {
            navigate('/');
        }
    };

    const handleCloseSuccessDialog = () => {
        setSuccessDialog({ ...successDialog, open: false });
        navigate('/');
    };

    const boxBackground = theme.palette.mode === 'dark' ? '#121212' : '#fafafa';

    if (initializing) {
        return (
            <Box
                sx={{
                    position: 'relative',
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                }}
            >
                {/* Video background */}
                <video
                    autoPlay
                    muted
                    loop
                    style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        zIndex: -2,
                    }}
                >
                    <source src={loginVideo} type="video/mp4" />
                </video>

                {/* Dark overlay */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        zIndex: -1,
                    }}
                />

                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 400,
                        backgroundColor: boxBackground,
                        p: 4,
                        borderRadius: 2,
                        boxShadow: 3,
                        textAlign: 'center',
                        zIndex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <CircularProgress size={40} sx={{ mb: 2 }} />
                    <Typography variant="h6">
                        Verifying your reset link...
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (tokenError) {
        return (
            <Box
                sx={{
                    position: 'relative',
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                }}
            >
                {/* Video background */}
                <video
                    autoPlay
                    muted
                    loop
                    style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        zIndex: -2,
                    }}
                >
                    <source src={loginVideo} type="video/mp4" />
                </video>

                {/* Dark overlay */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        zIndex: -1,
                    }}
                />

                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 400,
                        backgroundColor: boxBackground,
                        p: 4,
                        borderRadius: 2,
                        boxShadow: 3,
                        textAlign: 'center',
                        zIndex: 1,
                    }}
                >
                    <Typography component="h1" variant="h5" color="error" gutterBottom>
                        Invalid Reset Link
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                        The password reset link is invalid or has expired.
                    </Typography>
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={() => navigate('/')}
                        sx={{ mt: 1 }}
                    >
                        Back to Login
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <>
            <Box
                component="form"
                onSubmit={handleResetPassword}
                sx={{
                    position: 'relative',
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                }}
            >
                {/* Video background */}
                <video
                    autoPlay
                    muted
                    loop
                    style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        zIndex: -2,
                    }}
                >
                    <source src={loginVideo} type="video/mp4" />
                </video>

                {/* Dark overlay */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        zIndex: -1,
                    }}
                />

                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 400,
                        backgroundColor: boxBackground,
                        p: 4,
                        borderRadius: 2,
                        boxShadow: 3,
                        textAlign: 'center',
                        zIndex: 1,
                    }}
                >
                    <Typography component="h1" variant="h5" gutterBottom>
                        Reset Your Password
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                        Please enter your new password below
                    </Typography>

                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="New Password"
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        autoComplete="new-password"
                        value={password}
                        onChange={handlePasswordChange}
                        error={!!passwordError}
                        helperText={passwordError}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleClickShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="confirmPassword"
                        label="Confirm Password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        error={!!confirmPasswordError}
                        helperText={confirmPasswordError}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleClickShowConfirmPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                    >
                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{ mt: 3, mb: 2 }}
                        disabled={loading || !!passwordError || !!confirmPasswordError}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Reset Password'}
                    </Button>

                    <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        onClick={() => navigate('/')}
                        sx={{ mt: 1 }}
                    >
                        Back to Login
                    </Button>
                </Box>
            </Box>

            {/* Error Dialog */}
            <Dialog open={errorDialog.open} onClose={handleCloseErrorDialog}>
                <DialogTitle>Error</DialogTitle>
                <DialogContent>
                    <Typography>{errorDialog.message}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseErrorDialog} color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Dialog */}
            <Dialog open={successDialog.open} onClose={handleCloseSuccessDialog}>
                <DialogTitle>Success</DialogTitle>
                <DialogContent>
                    <Typography>{successDialog.message}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseSuccessDialog} color="primary">
                        Go to Login
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ResetPasswordPage; 